import type { AccountUser, BoardMemberInfo } from '@doriavboard/shared';
import { UserMinus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { errorMessage } from '../api/client';
import { accountLabel } from '../lib/users';
import { useSession } from '../stores/session';
import { Avatar } from './ui/Avatar';
import { Button } from './ui/Button';
import { Input, Select } from './ui/Input';
import { Spinner } from './ui/Spinner';

type MemberRole = 'editor' | 'viewer';

interface PeopleWithAccessProps {
  /** Null while loading. */
  people: { owner: AccountUser; members: BoardMemberInfo[] } | null;
  /** Owners can add, change and remove people; everyone else only sees them. */
  canManage: boolean;
  onInvite(username: string, role: MemberRole): Promise<void>;
  onChangeRole(userId: string, role: MemberRole): void;
  onRemove(userId: string): void;
}

/** The invite form and the list of who has access, shared by boards and spaces. */
export function PeopleWithAccess({
  people,
  canManage,
  onInvite,
  onChangeRole,
  onRemove,
}: PeopleWithAccessProps) {
  const me = useSession((s) => s.user);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<MemberRole>('editor');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function invite(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setInviteError(null);
    try {
      await onInvite(username, role);
      setUsername('');
    } catch (err) {
      setInviteError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {canManage && (
        <form onSubmit={invite} className="mb-5 flex items-start gap-2">
          <div className="flex-1">
            <Input
              autoFocus
              placeholder="Add people by username"
              aria-label="Username"
              autoCapitalize="none"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/^@/, ''))}
              error={inviteError}
              required
            />
          </div>
          <Select
            aria-label="Role"
            className="h-10"
            value={role}
            onChange={(e) => setRole(e.target.value as MemberRole)}
          >
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </Select>
          <Button type="submit" variant="primary" loading={busy}>
            Invite
          </Button>
        </form>
      )}

      <h3 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        People with access
      </h3>
      {!people ? (
        <div className="flex justify-center py-6">
          <Spinner className="size-6 text-slate-400" />
        </div>
      ) : (
        <ul className="mb-5 flex max-h-64 flex-col gap-1 overflow-y-auto">
          <li className="flex items-center gap-3 rounded-lg px-1 py-1.5">
            <Avatar name={people.owner.name} color={people.owner.color} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {people.owner.name}{' '}
                {people.owner.id === me?.id && <span className="text-slate-400">(you)</span>}
              </p>
              <p className="truncate text-xs text-slate-500">{accountLabel(people.owner)}</p>
            </div>
            <span className="text-sm text-slate-500">Owner</span>
          </li>
          {people.members.map(({ user, role: memberRole }) => (
            <li key={user.id} className="flex items-center gap-3 rounded-lg px-1 py-1.5">
              <Avatar name={user.name} color={user.color} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {user.name} {user.id === me?.id && <span className="text-slate-400">(you)</span>}
                </p>
                <p className="truncate text-xs text-slate-500">{accountLabel(user)}</p>
              </div>
              {canManage ? (
                <>
                  <Select
                    aria-label={`Role of ${user.name}`}
                    value={memberRole}
                    onChange={(e) => onChangeRole(user.id, e.target.value as MemberRole)}
                  >
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </Select>
                  <button
                    type="button"
                    onClick={() => onRemove(user.id)}
                    className="focus-ring rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                    aria-label={`Remove ${user.name}`}
                  >
                    <UserMinus className="size-4" />
                  </button>
                </>
              ) : (
                <span className="text-sm text-slate-500 capitalize">{memberRole}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
